import {Component, EventEmitter, Input, OnInit, Optional, Output, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {Address} from 'ngx-google-places-autocomplete/objects/address';
import {GooglePlaceDirective} from 'ngx-google-places-autocomplete';
import {FieldService, ValidationService} from 'front-base-angular-core/angular/projects/front-base-angular-core/src/public-api';
import {MapsService} from '@shared/services/maps.service';
import {Options} from 'ngx-google-places-autocomplete/objects/options/options';
import {localeKeyShort} from '@shared/constants/i18n.constants';

@Component({
  selector: 'si-google-address-searcher',
  templateUrl: './si-google-address-searcher.component.html',
  styleUrls: ['./si-google-address-searcher.component.scss']
})
export class GoogleAddressSearcher implements OnInit {
  @Input() ngxPlacesInputOptions = {
    componentRestrictions: {country: localeKeyShort.ES}
  } as Options;
  @Input() text: string;
  @Input() placeholder: string;
  @Input() styleClass: string;
  @Input() id: string;
  @Input() name: string;
  @Input() disabled: boolean;
  @Input() tooltipText: string;
  @Input() tooltipPosition: string;
  @Input() tooltipIcon: string;
  @Input() tooltipStyleClass: string;
  @Input() tooltipEscape = true;
  @Input() rule: string;
  @Input() validationExternalErrorText: string;
  //Error message to display when the input model does not validate the ruleRegex input.
  @Input() ruleRegexErrorText: string;
  @Input() autoFocus: boolean;
  @Input() hideLabel = false;
  @Input() defaultAddress: string;
  @Input() defaultCoords: [number, number];
  @Output() modelChange = new EventEmitter<Address>();
  @ViewChild('placesRef', {read: GooglePlaceDirective}) placesRef: GooglePlaceDirective;
  @ViewChild('formModel') formModel: NgForm;
  showRuleErrorOutsideIcon: boolean;
  validationErrorText: string;
  updateNgModelOnBlur: boolean;
  private _ruleRegex: string;
  private _model: Address;

  constructor(
    @Optional() private form: NgForm,
    private validationService: ValidationService,
    private fieldService: FieldService,
    private mapsService: MapsService
  ) {
    this.setLogic();
  }

  get model() {
    return this._model;
  }

  @Input()
  set model(val) {
    this._model = val;
    if (val?.formatted_address && this.placesRef) {
      this.placesRef['el'].nativeElement.value = val.formatted_address;
    }
  }

  get ruleRegex(): string {
    return this._ruleRegex;
  }

  // Regex to validate the model.
  @Input()
  set ruleRegex(val: string) {
    this._ruleRegex = val;
    this.validateRegex(this.model.formatted_address);
  }

  ngOnInit(): void {
    this._model = {formatted_address: this.defaultAddress} as Address;

    if (!this.defaultCoords?.[0] && !this.defaultCoords?.[1]) {
      this.useFirstPrediction(this.defaultAddress);
    }
  }

  setLogic(): void {
    this.showRuleErrorOutsideIcon = this.validationService.getShowRuleErrorOutsideIcon();
    this.updateNgModelOnBlur = this.fieldService.getInputUpdateNgModelOnBlur();
  }

  //This method has been added because the default method in ngx does not detect when the field is empty.
  //To avoid conflicts, it is checked that it only works when the field is empty.
  onChangeModel(model: any): void {
    if (model === '') {
      this.modelChange.emit(model);
    }
  }

  public handleAddressChange(model: Address): void {
    this.validateRegex(model.formatted_address);
    this.modelChange.emit(model);
  }

  validateRegex(model: string): void {
    if (!model || !this.ruleRegex) {
      this.validationExternalErrorText = null;
      return;
    }

    const regex = new RegExp(this.ruleRegex);
    this.validationExternalErrorText = regex.test(model) ? null : this.ruleRegexErrorText;
  }

  async useFirstPrediction(text: string): Promise<void> {
    const address = await this.mapsService.getFirstPrediction(text, this.ngxPlacesInputOptions);
    this._model = address;
    this.modelChange.emit(address);
  }
}
